import { p as proxyCustomElement, H, c as createEvent, h, a as Host } from './index.js';
import { w as waitForAnimation } from './p-rUtJRH95.js';
import { d as defineCustomElement$5 } from './p-sF9rvBC9.js';
import { d as defineCustomElement$4 } from './p-BDIehgMZ.js';
import { d as defineCustomElement$3 } from './p-Trmgzgcp.js';
import { d as defineCustomElement$2 } from './p-KbTC_jnR.js';

const slideOutCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;bottom:0;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;left:0;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;position:fixed;right:0;top:0;z-index:var(--jw-z-index-overlays)}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host .container{align-items:center;bottom:0;display:flex;justify-content:center;left:0;min-height:100%;pointer-events:none;position:relative;right:0;top:0;z-index:var(--jw-z-index-overlays)}:host .backdrop{background-color:#000000b3;bottom:0;left:0;opacity:0;pointer-events:none;position:fixed;right:0;top:0;transition:opacity .25s ease;z-index:calc(var(--jw-z-index-overlays) - 1)}:host .backdrop.-anim-fade-in{animation:fade-in .25s linear forwards;pointer-events:all}@keyframes fade-in{0%{opacity:0}to{opacity:1}}:host .backdrop.-anim-fade-out{animation:fade-out .25s linear forwards}@keyframes fade-out{0%{opacity:1}to{opacity:0}}:host .content{pointer-events:all;z-index:calc(var(--jw-z-index-overlays) + 1)}.content{background-color:var(--jw-theme-surface-tile-bg-color);bottom:0;color:var(--jw-theme-surface-text-default-color);display:flex;flex-direction:column;height:100%;position:absolute;right:0;top:0;transform:translateX(100%);transition:box-shadow .2s linear .01s;width:var(--jw-container-lg)}@keyframes wui-slide-out-open{0%{opacity:0;transform:translateX(100%)}90%{opacity:1}to{opacity:1;transform:translateX(0)}}@keyframes wui-slide-out-close{0%{opacity:1;transform:translateX(0)}90%{opacity:0}to{opacity:0;transform:translateX(100%)}}.content.-anim-open{animation:wui-slide-out-open .5s cubic-bezier(.25,.46,.45,.94) forwards}.content.-anim-close{animation:wui-slide-out-close .5s cubic-bezier(.25,.46,.45,.94) forwards}.header{align-items:center;border-bottom:1px solid var(--jw-theme-surface-border-border-color);color:var(--jw-theme-surface-text-default-color);display:flex;flex:none;justify-content:space-between;padding:1.5rem}.header h2{font-family:Open Sans;font-size:1.25rem;font-weight:400;letter-spacing:-.0008em;line-height:1.75rem;margin:0;padding:0}.body{display:flex;flex:1;overflow-y:auto;padding:1.5rem}.body p{margin-top:0!important}.footer{align-items:center;border-top:1px solid var(--jw-theme-surface-border-border-color);display:flex;flex:none;gap:1rem;justify-content:flex-end;padding:1.5rem}:host([data-display=open]) .content{box-shadow:var(--jw-theme-effects-shadow-shadow-slideout-box-shadow)}::slotted([slot]){display:flex;flex:1;flex-direction:column}::slotted([slot=footer]){display:flex;flex:none;flex-direction:row;gap:1rem}";

const WuiSlideOut$1 = /*@__PURE__*/ proxyCustomElement(class WuiSlideOut extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.displayChanged = createEvent(this, "displayChanged", 3);
        /**
         * Slide-out title
         */
        this.headerTitle = '';
        /**
         * Whether to show a backdrop behind the slide-out, overlaying the rest of the viewport.
         */
        this.backdrop = true;
        /**
         * Slideout theme
         */
        this.theme = 'light';
        /**
         * Sections to render
         */
        this.sections = ['header', 'body', 'footer'];
        /**
         * Whether slide-out must be explicitly dismissed by an action.
         *
         * A blocking slide-out will prevent any other page interaction until the slide-out is dismissed.
         */
        this.blocking = false;
        this.contentAnimClass = '';
        this.backdropAnimClass = '';
        this.display = 'closed';
        this.animate = async ({ backdrop, content, }) => {
            const shadowRoot = this.elm.shadowRoot;
            if (!shadowRoot) {
                return;
            }
            const promise = Promise.all([
                backdrop ? waitForAnimation(shadowRoot.querySelector('.content')) : Promise.resolve(),
                content ? waitForAnimation(shadowRoot.querySelector('.backdrop')) : Promise.resolve(),
            ]);
            if (backdrop) {
                this.backdropAnimClass = backdrop;
            }
            if (content) {
                this.contentAnimClass = content;
            }
            await promise;
        };
        this.handleDismiss = () => {
            // Can't dismiss blocking slide-outs
            if (this.blocking) {
                return;
            }
            this._setDisplay('closed');
        };
    }
    /**
     * Set display state.
     *
     * Possible values: `'open'`, `'closed'`.
     */
    async setDisplay(value) {
        if (value === this.display) {
            return;
        }
        return this._setDisplay(value);
    }
    _setDisplay(value) {
        return new Promise((resolve) => {
            window.requestAnimationFrame(() => {
                switch (value) {
                    case 'open':
                        this.animate({
                            backdrop: '-anim-fade-in',
                            content: '-anim-open',
                        }).then(() => {
                            this.displayChanged.emit(this.display);
                            resolve();
                        });
                        this.display = 'open';
                        break;
                    case 'closed':
                        this.animate({
                            backdrop: '-anim-fade-out',
                            content: '-anim-close',
                        }).then(() => {
                            // Wait for animations to finish before resetting state
                            window.requestAnimationFrame(() => {
                                this.display = 'closed';
                                this.displayChanged.emit(this.display);
                                resolve();
                            });
                        });
                        break;
                    default:
                        resolve();
                        break;
                }
            });
        });
    }
    render() {
        const { headerTitle, backdrop, blocking, uid, display, contentAnimClass, backdropAnimClass } = this;
        return (h(Host, { key: '36d791f82e13f4f3bc339464794528f50129158d', id: uid ? `wui_slide_out__${uid}` : '', "data-display": display, theme: this.theme }, backdrop && h("div", { key: 'ad7bd961db0da583e87236b0f0cea8d40e31807c', class: `backdrop ${backdropAnimClass}`, role: "button", onClick: this.handleDismiss }), h("div", { key: '20fa0ec1a3cf97b55210dc0a34d5509fd20e8ee3', class: "container" }, h("div", { key: '8d9782fd96918bd057a40c6757a7ac1118bccd57', class: `content ${contentAnimClass}` }, (!this.sections || this.sections.includes('header')) && (h("div", { key: '2f12ee0a935aa27092161013a0ab2251e896ac15', class: "header" }, h("h2", { key: 'aa38e9abef9b0da3d84567baf18aba207d59a17c' }, headerTitle), !blocking && h("wui-close-button", { key: '68dbe787719d048f00279952e440abd57e6f16a5', onClick: this.handleDismiss }))), (!this.sections || this.sections.includes('body')) && (h("div", { key: '4c4dd88fdc3ef7bd2bf9ac1fb0935e79a98a9414', class: "body" }, h("slot", { key: 'be7a580b341f54900654790332e66630b444a36a', name: "body" }))), (!this.sections || this.sections.includes('footer')) && (h("div", { key: '7102b5046c003992320a6ebe78a26c5a8bd8c3be', class: "footer" }, h("slot", { key: '8c79365e1313bd67e6b602dcc479f7eb1c2a5aa6', name: "alert" }), h("slot", { key: '38cb5f7298c355dd87835fcf08ea6aa58c1a6d80', name: "footer" })))))));
    }
    get elm() { return this; }
    static get style() { return slideOutCss; }
}, [257, "wui-slide-out", {
        "uid": [1],
        "api": [16],
        "headerTitle": [1, "header-title"],
        "backdrop": [4],
        "theme": [1],
        "sections": [16],
        "blocking": [4],
        "contentAnimClass": [32],
        "backdropAnimClass": [32],
        "display": [32],
        "setDisplay": [64]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-slide-out", "wui-close-button", "wui-icon", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-slide-out":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiSlideOut$1);
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiSlideOut = WuiSlideOut$1;
const defineCustomElement = defineCustomElement$1;

export { WuiSlideOut, defineCustomElement };
